import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { branchSummary } from '../../shared/branch-name';

export type TestRunEnvironment = 'TEST' | 'STAGING' | 'PRODUCTION' | 'LOCAL';

const KNOWN: readonly string[] = ['TEST', 'STAGING', 'PRODUCTION', 'LOCAL'];

/**
 * Umgebung eines Testlaufs als Risiko-Skala: Test neutral, Staging Vorstufe,
 * Production kritisch. `LOCAL` ist im Design-Bundle nicht gestaltet und
 * übernimmt die neutrale Variante.
 *
 * Bestandsdaten tragen auch Werte außerhalb dieser vier (etwa
 * `local-postgresql-16-mocked-integrations`); die bleiben lesbar und fallen
 * ebenfalls auf die neutrale Variante zurück. Pipeline-Läufe tragen hier den
 * Branch (`ObservedRunPersister`) — `fix/NEX-1329-objektreferenz-...` sprengt
 * jede Spaltenbreite. In der Tabelle zaehlen davon zwei Dinge: die Art des
 * Branches und das Ticket dahinter — die Pill zeigt `fix NEX-1329` und reicht
 * den vollen Namen über `title` und einen optisch versteckten Text nach — ein
 * `aria-label` an einem `<span>` ohne Rolle ignorieren Screenreader (ARIA 1.2).
 * Nennt der Branch kein
 * Ticket, bleibt es beim Typ; ein Branch ohne Typ (`develop`) steht ganz da. Mit
 * `isBranch` tritt ausserdem an die Stelle des Umgebungspunktes ein
 * Verzweigungssymbol: derselbe Platz, aber ohne die Risiko-Lesart, die einer
 * Umgebung zusteht und einem Branch nicht.
 */
@Component({
  selector: 'app-umgebung-pill',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      class="umgebung-pill"
      [attr.data-umgebung]="normalized()"
      [attr.data-testid]="testId()"
      [attr.title]="titleText()"
    >
      @if (isBranch()) {
        <span class="umgebung-pill__icon material-icons" aria-hidden="true">call_split</span>
      } @else {
        <span class="umgebung-pill__dot" aria-hidden="true"></span>
      }
      @if (fullValue()) {
        <span class="umgebung-pill__label" aria-hidden="true">{{ displayLabel() }}</span>
        <span class="umgebung-pill__full">{{ fullValue() }}</span>
      } @else {
        <span class="umgebung-pill__label">{{ displayLabel() }}</span>
      }
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
      max-width: 100%;
      min-width: 0;
    }

    .umgebung-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.4375rem;
      padding: 0.3125rem 0.6875rem;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;
      box-sizing: border-box;
      max-width: 100%;
      min-width: 0;
      background: #f1f5f9;
      color: #475569;

      &[data-umgebung='STAGING'] {
        background: #fef3c7;
        color: #92400e;

        .umgebung-pill__dot {
          background: #f59e0b;
        }
      }

      &[data-umgebung='PRODUCTION'] {
        background: #fee2e2;
        color: #b91c1c;

        .umgebung-pill__dot {
          background: #dc2626;
        }
      }
    }

    .umgebung-pill__label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .umgebung-pill__full {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }

    // Laedt die Icon-Font nicht, rendert die Ligatur als Wort 'call_split' und
    // schoebe die Pill aus der Spalte. Erst display/overflow halten das
    // Kaestchen bei 0.75rem, Glyph hin oder her.
    .umgebung-pill__icon {
      display: inline-block;
      overflow: hidden;
      font-size: 0.75rem;
      width: 0.75rem;
      height: 0.75rem;
      flex-shrink: 0;
    }

    .umgebung-pill__dot {
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 50%;
      flex-shrink: 0;
      background: #94a3b8;
    }
  `,
})
export class UmgebungPillComponent {
  private readonly translate = inject(TranslateService);

  readonly umgebung = input.required<string>();

  /** Pipeline-Laeufe tragen hier einen Branch statt einer Umgebung (NEX-1332). */
  readonly isBranch = input(false);

  readonly normalized = computed(() => (this.umgebung() ?? '').toUpperCase());
  readonly testId = computed(() => `umgebung-pill-${this.normalized().toLowerCase()}`);

  private readonly known = computed(() => KNOWN.includes(this.normalized()));

  /** Unbekannte Werte werden roh gezeigt — besser als ein leeres Feld. */
  readonly displayLabel = computed(() => {
    if (this.known()) {
      return this.translate.instant(`testRun.environment.${this.normalized()}`);
    }
    const raw = this.umgebung() ?? '';
    return this.isBranch() ? branchSummary(raw) : raw;
  });

  /**
   * Rohwerte koennen auf jeder Spaltenbreite abgeschnitten werden — sei es vom
   * gekappten Prefix oder vom Ellipsis. Deshalb haengt der volle Wert immer an
   * ihnen; die vier bekannten Umgebungen stehen ohnehin ganz da.
   */
  readonly fullValue = computed(() => (this.known() ? null : (this.umgebung() ?? '')));

  /** Am Zeiger steht immer der ungekuerzte Wert — bei bekannten Umgebungen deren Uebersetzung. */
  readonly titleText = computed(() => this.fullValue() ?? this.displayLabel());
}
